import Vue from 'vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, email, length, min_value, max_value, double, numeric, alpha } from 'vee-validate/dist/rules'
import { EMPTY_VALUES } from './constants/emptyValues'

Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)

extend('required', {
  ...required,
  message: 'This field is required'
})
extend('email', {
  ...email,
  message: 'This field must be a valid email address'
})
extend('length', {
  ...length,
  message: 'This field must be {length} characters long'
})

extend('min_value', {
  ...min_value,
  message: 'This field must be at least {min}'
})

extend('max_value', {
  ...max_value,
  message: 'This field must be at most {max}'
})

extend('positive_integer', {
  validate: (value) => {
    return +value >= 0
  },
  message: 'This field must be a valid positive integer'
})

extend('uuidv4', {
  validate: (value) => {
    return /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(value)
  },
  message: 'This field must be a valid UUID v4'
})

extend('uuidv7', {
  validate: (value) => {
    return /^[0-9A-F]{8}-[0-9A-F]{4}-7[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(value)
  },
  message: 'This field must be a valid UUID v7'
})

extend('phone', {
  validate: (value) => {
    return /^\+[1-9]\d{4,14}$/.test(value)
  },
  message: 'This field must be a valid phone number including "+", country code and your number'
})

extend('double', {
  ...double,
  message: 'This field must be a valid decimal'
})

extend('numeric', {
  ...numeric,
  message: (_, { _value_ }) => {
    if (Array.isArray(_value_)) {
      return 'This field must contain only numbers'
    }

    return 'This field must be a valid positive number'
  }
})

extend('unique_threshold', {
  params: ['thresholds'],
  validate: (value, { thresholds }) => {
    const thresholdsArray = []

    if (Array.isArray(thresholds)) {
      thresholdsArray.push(...thresholds)
    } else if (thresholds) {
      thresholdsArray.push(thresholds)
    }

    if (EMPTY_VALUES.includes(value) || !thresholds) {
      return true
    }

    const duplicateCount = thresholdsArray.filter(t => parseFloat(t.threshold) === parseFloat(value)).length
    return duplicateCount <= 1
  },
  message: 'This threshold amount is already used'
})

extend('no_digits', {
  validate: (value) => {
    return /^[^\d]*$/.test(value)
  },
  message: 'This field cannot contain any numbers'
})

extend('iban', {
  validate: (value) => {
    return /^AL\d{10}[0-9A-Z]{16}$|^AD\d{10}[0-9A-Z]{12}$|^AT\d{18}$|^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$|^BE\d{14}$|^BA\d{18}$|^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$|^HR\d{19}$|^CY\d{10}[0-9A-Z]{16}$|^CZ\d{22}$|^DK\d{16}$|^FO\d{16}$|^GL\d{16}$|^DO\d{2}[0-9A-Z]{4}\d{20}$|^EE\d{18}$|^FI\d{16}$|^FR\d{12}[0-9A-Z]{11}\d{2}$|^GE\d{2}[A-Z]{2}\d{16}$|^DE\d{20}$|^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$|^GR\d{9}[0-9A-Z]{16}$|^HU\d{26}$|^IS\d{24}$|^IE\d{2}[A-Z]{4}\d{14}$|^IL\d{21}$|^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^[A-Z]{2}\d{5}[0-9A-Z]{13}$|^KW\d{2}[A-Z]{4}22!$|^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$|^LB\d{6}[0-9A-Z]{20}$|^LI\d{7}[0-9A-Z]{12}$|^LT\d{18}$|^LU\d{5}[0-9A-Z]{13}$|^MK\d{5}[0-9A-Z]{10}\d{2}$|^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$|^MR13\d{23}$|^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$|^MC\d{12}[0-9A-Z]{11}\d{2}$|^ME\d{20}$|^NL\d{2}[A-Z]{4}\d{10}$|^NO\d{13}$|^PL\d{10}[0-9A-Z]{,16}n$|^PT\d{23}$|^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$|^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^SA\d{4}[0-9A-Z]{18}$|^RS\d{20}$|^SK\d{22}$|^SI\d{17}$|^ES\d{22}$|^SE\d{22}$|^CH\d{7}[0-9A-Z]{12}$|^TN59\d{20}$|^TR\d{7}[0-9A-Z]{17}$|^AE\d{21}$|^GB\d{2}[A-Z]{4}\d{14}$/i.test(value)
  },
  message: 'This field must be a valid IBAN'
})
